import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { LoadingSpinner, InlineLoadingState } from '../../../components/LoadingSpinner';
import { AppModal } from '../../../components/AppModal';
import { AppSelectDropdown } from '../../../components/AppSelectDropdown';
import { UI_Z_INDEX } from '../../../utils/uiZIndex';
import {
  SETTINGS_BTN_DANGER,
  SETTINGS_BTN_OUTLINE,
  SETTINGS_FIELD_LABEL,
  SETTINGS_SELECT_TRIGGER_CLASS,
} from '../utils/settingsSectionStyles';

export type DeleteRolePresetTarget = {
  id: string;
  name: string;
  assigned_user_count?: number;
};

type AssignedMember = {
  user_id: string;
  display_name: string | null;
  email: string;
};

type PresetOption = { id: string; name: string };

const AVATAR_COLORS = [
  'bg-blue-500',
  'bg-violet-500',
  'bg-emerald-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-cyan-500',
  'bg-indigo-500',
];

const VISIBLE_MEMBER_LIMIT = 6;

function avatarColor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) hash = (hash * 31 + userId.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function getInitials(name: string | null, email: string): string {
  const src = (name?.trim() || email.split('@')[0] || '?').trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return src.slice(0, 2).toUpperCase() || '?';
}

function memberLabel(member: AssignedMember): string {
  const name = member.display_name?.trim();
  if (name) return name;
  const email = member.email.trim();
  if (email && !email.endsWith('@phone.invalid')) {
    return email.includes('@') ? email.split('@')[0]! : email;
  }
  return 'Nutzer';
}

type DeleteRolePresetDialogProps = {
  open: boolean;
  preset: DeleteRolePresetTarget | null;
  otherPresets: PresetOption[];
  onClose: () => void;
  onDeleted: () => void | Promise<void>;
};

export function DeleteRolePresetDialog({
  open,
  preset,
  otherPresets,
  onClose,
  onDeleted,
}: DeleteRolePresetDialogProps) {
  const [members, setMembers] = React.useState<AssignedMember[]>([]);
  const [loadingMembers, setLoadingMembers] = React.useState(false);
  const [reassignToId, setReassignToId] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [forceReassign, setForceReassign] = React.useState(false);

  const assignedCount = Math.max(
    forceReassign ? 1 : 0,
    preset?.assigned_user_count ?? 0,
    members.length,
  );
  const needsReassign = assignedCount > 0;

  const loadAssignedMembers = React.useCallback(async (presetId: string) => {
    setLoadingMembers(true);
    try {
      const res = await fetch('/api/me/members', { credentials: 'include' });
      const data = (await res.json().catch(() => null)) as {
        members?: Array<{
          user_id: string;
          display_name: string | null;
          email: string;
          role_preset_id?: string | null;
        }>;
        error?: string;
      } | null;
      if (!res.ok) throw new Error(data?.error ?? 'Nutzer konnten nicht geladen werden.');
      setMembers(
        (data?.members ?? [])
          .filter((m) => String(m.role_preset_id ?? '') === presetId)
          .map((m) => ({
            user_id: m.user_id,
            display_name: m.display_name,
            email: String(m.email ?? ''),
          })),
      );
    } finally {
      setLoadingMembers(false);
    }
  }, []);

  React.useEffect(() => {
    if (!open || !preset) return;
    setReassignToId('');
    setError(null);
    setMembers([]);
    setForceReassign(false);

    if ((preset.assigned_user_count ?? 0) <= 0) return;

    let cancelled = false;
    void (async () => {
      try {
        await loadAssignedMembers(preset.id);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Nutzer konnten nicht geladen werden.');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, preset, loadAssignedMembers]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  if (!open || !preset) return null;

  const visibleMembers = members.slice(0, VISIBLE_MEMBER_LIMIT);
  const overflowCount = Math.max(0, members.length - visibleMembers.length);
  const reassignOptions = otherPresets
    .filter((entry) => entry.id !== preset.id)
    .map((entry) => ({ value: entry.id, label: entry.name }));

  const canConfirm = needsReassign ? Boolean(reassignToId) && !loadingMembers : true;

  const confirmDelete = async () => {
    if (!canConfirm) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/me/role-presets/${preset.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          needsReassign && reassignToId ? { reassign_to_preset_id: reassignToId } : {},
        ),
      });
      const data = (await res.json().catch(() => null)) as {
        error?: string;
        assigned_count?: number | null;
      } | null;
      if (res.status === 409) {
        setForceReassign(true);
        try {
          await loadAssignedMembers(preset.id);
        } catch {
          /* keep blocker UI even if member list fails */
        }
        setError(data?.error ?? 'Preset ist noch Nutzern zugewiesen. Bitte zuerst neu zuweisen.');
        return;
      }
      if (!res.ok) throw new Error(data?.error ?? 'Rolle konnte nicht gelöscht werden.');
      await onDeleted();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppModal
      open={open}
      onClose={saving ? () => {} : onClose}
      closeOnOverlayClick={!saving}
      disableClose={saving}
      overlayClassName="z-[100]"
      panelClassName="md:max-w-[38.4rem]"
      ariaLabelledBy="delete-role-preset-title"
    >
      <div className="flex h-full flex-col p-6 md:h-auto md:min-h-0">
        {needsReassign ? (
          <>
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-full bg-red-50 p-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h2 id="delete-role-preset-title" className="text-lg font-semibold text-app-text">
                  „{preset.name}“ kann nicht gelöscht werden
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-app-text-muted">
                  Diesem Preset {assignedCount === 1 ? 'ist' : 'sind'}{' '}
                  <span className="font-semibold text-app-text">
                    {assignedCount} Nutzer
                  </span>{' '}
                  zugewiesen. Weise {assignedCount === 1 ? 'ihn' : 'sie'} zuerst einem anderen Preset zu — sonst{' '}
                  {assignedCount === 1 ? 'verliert er' : 'verlieren sie'} den App-Zugang.
                </p>
              </div>
            </div>

            <div className="mt-4 min-h-[2.5rem]">
              {loadingMembers ? (
                <InlineLoadingState label="Zugewiesene Nutzer werden geladen…" className="justify-start" />
              ) : members.length > 0 ? (
                <div className="flex flex-wrap items-center gap-2">
                  {visibleMembers.map((member) => {
                    const label = memberLabel(member);
                    return (
                      <span
                        key={member.user_id}
                        className="inline-flex items-center gap-1.5 rounded-full border border-app-border bg-app-surface-muted py-1 pl-1 pr-2.5 text-sm text-app-text-muted"
                      >
                        <span
                          className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold text-white ${avatarColor(member.user_id)}`}
                        >
                          {getInitials(member.display_name, member.email)}
                        </span>
                        {label}
                      </span>
                    );
                  })}
                  {overflowCount > 0 ? (
                    <span className="inline-flex items-center rounded-full border border-app-border bg-app-surface-muted px-2.5 py-1 text-sm font-medium text-app-text-subtle">
                      +{overflowCount} weitere
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="mt-5">
              <label className={`mb-1.5 block ${SETTINGS_FIELD_LABEL}`}>
                Alle {assignedCount} Nutzer stattdessen zuweisen zu
              </label>
              <AppSelectDropdown
                value={reassignToId}
                onChange={setReassignToId}
                options={reassignOptions}
                placeholder="Preset wählen…"
                disabled={saving || reassignOptions.length === 0}
                menuZIndex={UI_Z_INDEX.portalDropdownInModal}
                triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
              />
              {reassignOptions.length === 0 ? (
                <p className="mt-2 text-sm text-amber-700">
                  Es gibt keine andere Rolle zum Umziehen. Lege zuerst eine neue Rolle an.
                </p>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <h2 id="delete-role-preset-title" className="text-lg font-semibold text-app-text">
              Rolle „{preset.name}“ löschen?
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-app-text-muted">
              Die Rolle wird entfernt und steht danach nicht mehr zur Auswahl. Es sind derzeit keine Nutzer
              zugewiesen.
            </p>
          </>
        )}

        {error ? (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="mt-8 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className={`w-full sm:w-auto ${SETTINGS_BTN_OUTLINE}`}
          >
            Abbrechen
          </button>
          <button
            type="button"
            disabled={saving || !canConfirm}
            onClick={() => void confirmDelete()}
            className={`inline-flex w-full items-center justify-center gap-2 sm:w-auto ${SETTINGS_BTN_DANGER}`}
          >
            {saving ? <LoadingSpinner /> : null}
            {needsReassign ? 'Umziehen & löschen' : 'Rolle löschen'}
          </button>
        </div>
      </div>
    </AppModal>
  );
}
